/**
 * Cognito 登录封装,对应产品文档 §10(管理平台自身登录鉴权)。
 *
 * 用 amazon-cognito-identity-js 直接调用 Cognito User Pool(USER_SRP_AUTH 流程,
 * 与 CDK AuthStack 里 `authFlows: { userSrp: true }` 对应),不引入额外的
 * Amplify 框架依赖,保持前端依赖面小。
 *
 * MFA(§10 决策:强制 TOTP)登录流程:
 *   1. authenticateUser() 提交用户名密码
 *   2. 若账号是 `admin-create-user` 创建的新账号(状态 FORCE_CHANGE_PASSWORD,
 *      还在用 Cognito 自动生成/邮件发送的临时密码),Cognito 返回
 *      NEW_PASSWORD_REQUIRED 质询,需要调用 completeNewPasswordChallenge()
 *      设置正式密码,之后才会继续走 MFA 质询
 *   3. 若账号已绑定 TOTP,Cognito 返回 MFA_REQUIRED 质询,
 *      调用 sendMFACode(code, "SOFTWARE_TOKEN_MFA") 提交动态码
 *   4. 若账号尚未绑定 TOTP,Cognito 返回 MFA_SETUP 质询,
 *      需要走 associateSoftwareToken + verifySoftwareToken 绑定流程
 *      (本模块提供绑定所需的方法,页面层负责引导用户扫码)
 */
import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { config } from "../config";

let cachedPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!cachedPool) {
    cachedPool = new CognitoUserPool({
      UserPoolId: config.cognitoUserPoolId,
      ClientId: config.cognitoClientId,
    });
  }
  return cachedPool;
}

export type LoginOutcome =
  | { status: "SUCCESS"; session: CognitoUserSession }
  | { status: "NEW_PASSWORD_REQUIRED"; cognitoUser: CognitoUser }
  | { status: "MFA_REQUIRED"; cognitoUser: CognitoUser }
  | { status: "MFA_SETUP_REQUIRED"; cognitoUser: CognitoUser };

export function login(username: string, password: string): Promise<LoginOutcome> {
  const cognitoUser = new CognitoUser({ Username: username, Pool: getUserPool() });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve({ status: "SUCCESS", session }),
      onFailure: (err) => reject(err),
      // admin-create-user 创建的账号首次用临时密码登录会触发这个质询,
      // 缺少该回调会导致 amazon-cognito-identity-js 内部调用不存在的方法报错
      // ("newPasswordRequired is not a function")。
      newPasswordRequired: () => resolve({ status: "NEW_PASSWORD_REQUIRED", cognitoUser }),
      totpRequired: () => resolve({ status: "MFA_REQUIRED", cognitoUser }),
      mfaSetup: () => resolve({ status: "MFA_SETUP_REQUIRED", cognitoUser }),
    });
  });
}

/**
 * 首次登录(FORCE_CHANGE_PASSWORD 状态)设置正式密码,取代临时密码。
 * 设置成功后 Cognito 会继续走后续质询(MFA_REQUIRED/MFA_SETUP),
 * 通过 onSuccess/totpRequired/mfaSetup 回调统一走 LoginOutcome 分支,
 * 与 login() 的处理方式保持一致,页面层不需要区分调用来源。
 */
export function completeNewPassword(
  cognitoUser: CognitoUser,
  newPassword: string
): Promise<LoginOutcome> {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(
      newPassword,
      {},
      {
        onSuccess: (session) => resolve({ status: "SUCCESS", session }),
        onFailure: (err) => reject(err),
        totpRequired: () => resolve({ status: "MFA_REQUIRED", cognitoUser }),
        mfaSetup: () => resolve({ status: "MFA_SETUP_REQUIRED", cognitoUser }),
      }
    );
  });
}

export function submitMfaCode(
  cognitoUser: CognitoUser,
  code: string
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
      },
      "SOFTWARE_TOKEN_MFA"
    );
  });
}

/** 首次登录绑定 TOTP:第一步,生成绑定密钥供前端展示为二维码 */
export function associateSoftwareToken(cognitoUser: CognitoUser): Promise<string> {
  return new Promise((resolve, reject) => {
    cognitoUser.associateSoftwareToken({
      onFailure: (err) => reject(err),
      associateSecretCode: (secretCode) => resolve(secretCode),
    });
  });
}

/** 首次登录绑定 TOTP:第二步,验证用户输入的验证码,绑定成功后仍需完成登录会话 */
export function verifySoftwareToken(
  cognitoUser: CognitoUser,
  code: string
): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.verifySoftwareToken(code, "kiro-admin-platform", {
      onFailure: (err) => reject(err),
      onSuccess: () => {
        cognitoUser.getSession((err: Error | null, session: CognitoUserSession) => {
          if (err) reject(err);
          else resolve(session);
        });
      },
    });
  });
}

export function getCurrentUser(): CognitoUser | null {
  return getUserPool().getCurrentUser();
}

export function getIdToken(): Promise<string | null> {
  const user = getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function getUserGroups(session: CognitoUserSession): string[] {
  const payload = session.getIdToken().decodePayload() as Record<string, unknown>;
  const groups = payload["cognito:groups"];
  return Array.isArray(groups) ? (groups as string[]) : [];
}

export function logout(): void {
  const user = getCurrentUser();
  user?.signOut();
}
